/** @jsx jsx */

/** @jsxFrag React.Fragment */
import type { Interpolation } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo, useState } from 'react';

import { CollectionsModule } from '@coursera-web/cds-labs';
import type { Filter } from '@coursera-web/cds-labs';

import { useLocation } from 'js/lib/useRouter';

import type { Theme } from '@coursera/cds-core';

import type { withAccomplishmentsAndPaymentsRecommendationDataProps } from 'bundles/accomplishments/api/accomplishmentsAndPaymentsRecommendationData';
import accomplishmentsAndPaymentsRecommendationSwitcher from 'bundles/accomplishments/api/accomplishmentsAndPaymentsRecommendationSwitcher';
import DegreesCollection from 'bundles/accomplishments/components/DegreesCollection';
import { CourseraPlusAdContainer } from 'bundles/accomplishments/components/hub/CourseraPlusAdContainer';
import { convertPathnameToSlug } from 'bundles/accomplishments/utils/routingUtils';
import withPremiumProductCollections from 'bundles/browse/components/Domain/withPremiumProductCollections';
import type { PremiumProductProps } from 'bundles/browse/components/Domain/withPremiumProductCollections';
import type {
  DiscoveryCollection,
  DiscoveryCollectionEntity,
} from 'bundles/browse/components/types/DiscoveryCollections';
import { ProductVariantNames } from 'bundles/browse/constants';
import type { CarouselCollection, CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import { mapCollectionsForCarousel } from 'bundles/collections-carousel/utils/mappingUtils';
import { CollectionsContainer } from 'bundles/collections-common';
import { DISCO_ENTITY_TO_PRODUCT_TYPES } from 'bundles/collections-common/utils/collectionsUtils';
import { filterUndefined } from 'bundles/common/utils/commonUtils';
import { getShouldShowCourseraPlusMerchandisingPlacements } from 'bundles/coursera-plus/utils/subscriptionsForwardDiscoveryExperiment';
import { useCourseraPlusProductOwnerships } from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import CertificateRecommendations from 'bundles/premium-certificate/components/CertificateRecommendations';
import CPlusElevateLTVPromoUnit from 'bundles/unified-seo-content-common/components/promo-unit/CPlusElevateLTVPromoUnit';

import _t from 'i18n!nls/accomplishments';

type BaseProps = {
  recommendationCollections?: Array<DiscoveryCollection | null>;
  loadingRecommendations?: boolean;
};

type PremiumProductCollectionProps = {
  mastertrackListFromPremiumProductsCollection?: PremiumProductProps[];
  universityCertificateListFromPremiumProductsCollection?: PremiumProductProps[];
  premiumProductsCollectionLoading?: boolean;
};

type Props = BaseProps & PremiumProductCollectionProps;

const styles: Record<string, Interpolation<Theme>> = {
  recommendationsWrapper: () => css`
    .collection-name {
      font-weight: 600;
      margin-left: 10px !important;
      font-family: 'Source Sans Pro', Arial, sans-serif;
    }

    .recommendation-modules-container .collection-name {
      margin-left: 10px !important;
    }

    .rc-desktopCollection {
      .rc-BrowseCarousel {
        max-width: 100%;

        .slick-slider {
          width: 100%;
        }

        .slick-list {
          margin: 0;
        }
      }
    }

    .rc-Collection {
      .rc-desktopCollection {
        @media screen and (max-width: 768px) {
          max-height: 262px;
        }
      }
    }

    .adContainer {
      @media screen and (min-width: 769px) {
        margin-left: 10px;
      }
    }
  `,

  cPlusElevateLTVPromoUnit: css`
    margin: 0 calc(var(--cds-spacing-200) * -1);
  `,
};

const mapDiscoveryToCollectionEntity = (entity: DiscoveryCollectionEntity): CollectionEntity => {
  return {
    ...entity,
    link: entity?.url,
    imageUrl: entity?.imageUrl || '',
    partners:
      entity?.partners?.filter(filterUndefined)?.map((partner) => ({ ...partner, logo: partner.logo || '' })) || [],
    partnerLogos: entity?.partners?.filter(filterUndefined)?.map((partner) => partner.logo || '') || [],
    isPartOfCourseraPlus: ('isPartOfCourseraPlus' in entity && entity?.isPartOfCourseraPlus) || false,
    isCostFree: ('isCostFree' in entity && entity?.isCostFree) || false,
    productType: DISCO_ENTITY_TO_PRODUCT_TYPES[entity.__typename],
  };
};

const mapDiscoveryToCarouselCollection = (collection: DiscoveryCollection): CarouselCollection => ({
  ...collection,
  collectionId: collection.id,
  title: collection?.label || '',
  entities: collection?.entities?.filter(filterUndefined)?.map(mapDiscoveryToCollectionEntity) || [],
});

const getDescriptionForCertRecommendations = (index: number) => {
  switch (index) {
    case 1:
      return _t('Earn an affordable, university-issued credential from top schools');
    case 0:
    default:
      return _t('Earn an university-issued credential and credit towards a degree');
  }
};

const AccomplishmentRecommendations = ({
  recommendationCollections,
  loadingRecommendations,
  mastertrackListFromPremiumProductsCollection,
  universityCertificateListFromPremiumProductsCollection,
  premiumProductsCollectionLoading,
}: Props) => {
  const location = useLocation();
  const slug = convertPathnameToSlug(location.pathname);

  const [currentCertificateCollectionSetIndex, setCurrentCertificateCollectionSetIndex] = useState(0);

  const showCollectionsModule = GrowthDiscoveryEpicClient.get('showLearningProductCard');

  const shouldShowCertRecommendations = useMemo(() => {
    const locationPage = location.pathname.split('/').filter((part) => part.length)[0];
    return locationPage === 'accomplishments';
  }, [location.pathname]);

  const { ownsCourseraPlus } = useCourseraPlusProductOwnerships();
  const shouldShowElevateLTVCourseraPlusBanner = getShouldShowCourseraPlusMerchandisingPlacements();

  recommendationCollections?.forEach((collection) => {
    if (collection?.id === 'RECOMMENDATIONS_MODULES:derived-day0-free-real-time-rec-algo$no_attribute$no_attribute') {
      collection.label = _t('Explore These Free Courses');
    }
  });

  const collections = recommendationCollections?.filter(filterUndefined).map(mapDiscoveryToCarouselCollection) ?? [];

  const certificateCollections = useMemo(() => {
    if (!mastertrackListFromPremiumProductsCollection && !universityCertificateListFromPremiumProductsCollection) {
      return [];
    }

    const mastertracksForCollectionCarousel = mastertrackListFromPremiumProductsCollection?.map((product) => ({
      degree: { ...product, id: product.id ?? product.slug },
      __typename: 'DegreeListsV3',
    }));

    const uCertsForCollectionCarousel = universityCertificateListFromPremiumProductsCollection?.map((product) => ({
      degree: { ...product, id: product.id ?? product.slug },
      __typename: 'DegreeListsV3',
    }));

    const unmappedCollections = [
      mastertracksForCollectionCarousel && {
        entries: mastertracksForCollectionCarousel,
        __typename: 'DegreeListV3Mastertrack',
        label: _t('MasterTrack® Certificates'),
      },
      uCertsForCollectionCarousel && {
        entries: uCertsForCollectionCarousel,
        __typename: 'DegreeListV3UniversityCertificate',
        label: _t('University Certificates'),
      },
    ].filter(Boolean);

    const mappedCollections = mapCollectionsForCarousel(unmappedCollections);
    return mappedCollections;
  }, [mastertrackListFromPremiumProductsCollection, universityCertificateListFromPremiumProductsCollection]);

  const certificateFilters: Filter[] = useMemo(() => {
    return [
      {
        id: 'mastertrack',
        filterName: _t('MasterTrack® Certificates'),
      },
      {
        id: 'university-certificate',
        filterName: _t('University Certificates'),
      },
    ];
  }, []);

  return (
    <section
      className="rc-AccomplishmentRecommendations"
      data-testid="accomplishmentRecommendations"
      css={styles.recommendationsWrapper}
    >
      <CollectionsContainer collections={collections} isCollectionsLoading={loadingRecommendations} />
      <DegreesCollection key="degree-collection" />
      {shouldShowCertRecommendations && showCollectionsModule && (
        <CollectionsModule
          collections={[certificateCollections[currentCertificateCollectionSetIndex]]}
          isCollectionsLoading={premiumProductsCollectionLoading}
          headline={_t('MasterTrack® and University Certificates')}
          headlineComponent="h2"
          layout="text-top/image-bottom"
          currentCollectionSetIndex={currentCertificateCollectionSetIndex}
          filters={certificateFilters}
          onFilterChange={(index) => setCurrentCertificateCollectionSetIndex(index)}
          description={getDescriptionForCertRecommendations(currentCertificateCollectionSetIndex)}
        />
      )}
      {shouldShowCertRecommendations && !showCollectionsModule && (
        <CertificateRecommendations
          title={_t('MasterTrack® and University Certificates')}
          productVariant={ProductVariantNames.Mastertrack}
          slug={slug}
        />
      )}
      {!ownsCourseraPlus && (
        <>
          {shouldShowElevateLTVCourseraPlusBanner ? (
            <div css={styles.cPlusElevateLTVPromoUnit}>
              <CPlusElevateLTVPromoUnit size="large" />
            </div>
          ) : (
            <CourseraPlusAdContainer />
          )}
        </>
      )}
    </section>
  );
};

const EnhancedAccomplishmentRecommendations =
  withPremiumProductCollections<withAccomplishmentsAndPaymentsRecommendationDataProps>(() => ({
    productVariants: [ProductVariantNames.Mastertrack, ProductVariantNames.UniversityCertificate],
  }))(AccomplishmentRecommendations);

export default accomplishmentsAndPaymentsRecommendationSwitcher(EnhancedAccomplishmentRecommendations);
